import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { ChevronUp, ChevronDown, Loader2, Check, X, Pencil } from "lucide-react";

interface ChecklistDefinition {
  id: string;
  name: string;
  description: string | null;
  isRequired: boolean;
  sortOrder: number;
  scrapedField: string | null;
  autoComplete: boolean;
  isActive: boolean;
  // Gate S6
  checklistType: "readiness" | "evaluation";
  evaluationTrackId: string | null;
}

interface EvaluationTrack {
  id: string;
  name: string;
  isDefault: boolean;
  isActive: boolean;
}

const QUERY_KEY = ["/api/admin/adoption-listings/definitions"];
const TRACKS_QUERY_KEY = ["/api/admin/evaluation-tracks"];

const addSchema = z.object({
  name: z.string().min(1, "Name is required").max(200),
  isRequired: z.boolean(),
  scrapedField: z.string().max(50).nullable(),
  autoComplete: z.boolean(),
  checklistType: z.enum(["readiness", "evaluation"]),
  evaluationTrackId: z.string().nullable(),
});

const SCRAPED_FIELD_OPTIONS: { value: string; label: string; types: Array<"readiness" | "evaluation"> }[] = [
  { value: "photo", label: "Photo", types: ["readiness"] },
  { value: "bio", label: "Bio", types: ["readiness"] },
  { value: "listing", label: "Listing", types: ["readiness"] },
  { value: "status", label: "Status", types: ["readiness"] },
  { value: "evaluation_status", label: "Evaluation status", types: ["evaluation"] },
];

export function AnimalProfilesSettingsCard() {
  const { toast } = useToast();

  const { data: defs = [], isLoading } = useQuery<ChecklistDefinition[]>({
    queryKey: QUERY_KEY,
  });

  const { data: tracks = [] } = useQuery<EvaluationTrack[]>({
    queryKey: TRACKS_QUERY_KEY,
  });

  const activeTracks = tracks.filter((t) => t.isActive);

  const patchMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: object }) =>
      apiRequest("PATCH", `/api/admin/adoption-listings/definitions/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: () => {
      toast({ title: "Error", description: "Could not update definition.", variant: "destructive" });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (orderedIds: string[]) =>
      apiRequest("POST", "/api/admin/adoption-listings/definitions/reorder", { orderedIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: () => {
      toast({ title: "Error", description: "Could not reorder definitions.", variant: "destructive" });
    },
  });

  const createMutation = useMutation({
    mutationFn: (payload: object) =>
      apiRequest("POST", "/api/admin/adoption-listings/definitions", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setNewName("");
      setNewIsRequired(true);
      setNewScrapedField("");
      setNewAutoComplete(false);
      setNewChecklistType("readiness");
      setNewEvaluationTrackId("");
      toast({ title: "Item added", description: "Checklist item created." });
    },
    onError: () => {
      toast({ title: "Error", description: "Could not add definition.", variant: "destructive" });
    },
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editingScrapedField, setEditingScrapedField] = useState("");
  const [editingAutoComplete, setEditingAutoComplete] = useState(false);
  const [editingEvaluationTrackId, setEditingEvaluationTrackId] = useState<string>("");

  const [newName, setNewName] = useState("");
  const [newIsRequired, setNewIsRequired] = useState(true);
  const [newScrapedField, setNewScrapedField] = useState("");
  const [newAutoComplete, setNewAutoComplete] = useState(false);
  const [newChecklistType, setNewChecklistType] = useState<"readiness" | "evaluation">("readiness");
  const [newEvaluationTrackId, setNewEvaluationTrackId] = useState("");

  const activeDefs = defs.filter((d) => d.isActive);
  const inactiveDefs = defs.filter((d) => !d.isActive);

  const readinessDefs = activeDefs.filter((d) => d.checklistType === "readiness");
  const evaluationDefs = activeDefs.filter((d) => d.checklistType === "evaluation");

  function startEdit(def: ChecklistDefinition) {
    setEditingId(def.id);
    setEditingName(def.name);
    setEditingScrapedField(def.scrapedField ?? "");
    setEditingAutoComplete(def.autoComplete);
    setEditingEvaluationTrackId(def.evaluationTrackId ?? "");
  }

  function cancelEdit() {
    setEditingId(null);
    setEditingName("");
    setEditingScrapedField("");
    setEditingAutoComplete(false);
    setEditingEvaluationTrackId("");
  }

  function commitEdit(def: ChecklistDefinition) {
    const trimmed = editingName.trim();
    if (!trimmed) return;
    const scrapedFieldValue = editingScrapedField.trim() || null;
    const autoCompleteValue = scrapedFieldValue ? editingAutoComplete : false;
    const payload: Record<string, unknown> = {
      name: trimmed,
      scrapedField: scrapedFieldValue,
      autoComplete: autoCompleteValue,
    };
    if (def.checklistType === "evaluation") {
      payload.evaluationTrackId = editingEvaluationTrackId || null;
    }
    patchMutation.mutate({ id: def.id, payload });
    setEditingId(null);
    setEditingName("");
    setEditingScrapedField("");
    setEditingAutoComplete(false);
    setEditingEvaluationTrackId("");
  }

  function handleReorder(id: string, direction: "up" | "down", section: "readiness" | "evaluation") {
    const sectionDefs = section === "readiness" ? readinessDefs : evaluationDefs;
    const ids = sectionDefs.map((d) => d.id);
    const idx = ids.indexOf(id);
    if (idx < 0) return;
    const newIds = [...ids];
    if (direction === "up" && idx > 0) {
      [newIds[idx - 1], newIds[idx]] = [newIds[idx], newIds[idx - 1]];
    } else if (direction === "down" && idx < newIds.length - 1) {
      [newIds[idx], newIds[idx + 1]] = [newIds[idx + 1], newIds[idx]];
    }
    reorderMutation.mutate(newIds);
  }

  function handleAdd() {
    const scrapedFieldValue = newScrapedField.trim() || null;
    const parsed = addSchema.safeParse({
      name: newName.trim(),
      isRequired: newIsRequired,
      scrapedField: scrapedFieldValue,
      autoComplete: scrapedFieldValue ? newAutoComplete : false,
      checklistType: newChecklistType,
      evaluationTrackId: newChecklistType === "evaluation" ? (newEvaluationTrackId || null) : null,
    });
    if (!parsed.success) {
      toast({ title: "Validation error", description: parsed.error.issues[0]?.message, variant: "destructive" });
      return;
    }
    createMutation.mutate({
      name: parsed.data.name,
      isRequired: parsed.data.isRequired,
      scrapedField: parsed.data.scrapedField,
      autoComplete: parsed.data.autoComplete,
      checklistType: parsed.data.checklistType,
      evaluationTrackId: parsed.data.evaluationTrackId,
    });
  }

  function getTrackName(trackId: string | null): string {
    if (!trackId) return "All tracks";
    return tracks.find((t) => t.id === trackId)?.name ?? "Unknown track";
  }

  function renderDefinitionRow(def: ChecklistDefinition, idx: number, sectionDefs: ChecklistDefinition[]) {
    return (
      <div
        key={def.id}
        className="flex items-center gap-3 py-3"
        data-testid={`row-definition-${def.id}`}
      >
        <div className="flex flex-col gap-0.5">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => handleReorder(def.id, "up", def.checklistType)}
            disabled={idx === 0 || reorderMutation.isPending}
            data-testid={`button-move-up-${def.id}`}
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => handleReorder(def.id, "down", def.checklistType)}
            disabled={idx === sectionDefs.length - 1 || reorderMutation.isPending}
            data-testid={`button-move-down-${def.id}`}
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </div>

        <div className="flex-1 min-w-0">
          {editingId === def.id ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Input
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitEdit(def);
                    if (e.key === "Escape") cancelEdit();
                  }}
                  className="h-8 text-sm"
                  autoFocus
                  placeholder="Item name"
                  data-testid={`input-edit-name-${def.id}`}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => commitEdit(def)}
                  disabled={patchMutation.isPending}
                  data-testid={`button-save-name-${def.id}`}
                >
                  {patchMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Check className="h-3.5 w-3.5" />
                  )}
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={cancelEdit}
                  data-testid={`button-cancel-edit-${def.id}`}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>

              <Select
                value={editingScrapedField || "__none__"}
                onValueChange={(v) => {
                  const val = v === "__none__" ? "" : v;
                  setEditingScrapedField(val);
                  if (!val) setEditingAutoComplete(false);
                }}
              >
                <SelectTrigger className="h-8 text-sm" data-testid={`select-edit-scraped-field-${def.id}`}>
                  <SelectValue placeholder="None (manual only)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None (manual only)</SelectItem>
                  {SCRAPED_FIELD_OPTIONS.filter((o) => o.types.includes(def.checklistType)).map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {def.checklistType === "evaluation" && (
                <Select
                  value={editingEvaluationTrackId || "__core__"}
                  onValueChange={(v) => setEditingEvaluationTrackId(v === "__core__" ? "" : v)}
                >
                  <SelectTrigger className="h-8 text-sm" data-testid={`select-edit-eval-track-${def.id}`}>
                    <SelectValue placeholder="All tracks (core item)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__core__">All tracks (core item)</SelectItem>
                    {activeTracks.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium truncate" data-testid={`text-def-name-${def.id}`}>
                {def.name}
              </span>
              {def.scrapedField && (
                <Badge variant="outline" className="text-xs shrink-0" data-testid={`badge-scraped-field-${def.id}`}>
                  {def.autoComplete ? "Auto-completes: " : "Tracks: "}{def.scrapedField}
                </Badge>
              )}
              {def.checklistType === "evaluation" && (
                <Badge variant="outline" className="text-xs shrink-0 text-muted-foreground" data-testid={`badge-eval-track-${def.id}`}>
                  {getTrackName(def.evaluationTrackId)}
                </Badge>
              )}
              <Button
                size="icon"
                variant="ghost"
                onClick={() => startEdit(def)}
                data-testid={`button-edit-name-${def.id}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 shrink-0">
          {def.scrapedField && (
            <div className="flex items-center gap-1.5">
              <Switch
                checked={def.autoComplete}
                onCheckedChange={(checked) =>
                  patchMutation.mutate({ id: def.id, payload: { autoComplete: checked } })
                }
                disabled={patchMutation.isPending}
                data-testid={`switch-auto-complete-${def.id}`}
              />
              <span className="text-xs text-muted-foreground w-[82px]">
                {def.autoComplete ? "Auto-complete" : "Manual only"}
              </span>
            </div>
          )}

          <div className="flex items-center gap-1.5">
            <Switch
              checked={def.isRequired}
              onCheckedChange={(checked) =>
                patchMutation.mutate({ id: def.id, payload: { isRequired: checked } })
              }
              disabled={patchMutation.isPending}
              data-testid={`switch-required-${def.id}`}
            />
            <span className="text-xs text-muted-foreground w-14">
              {def.isRequired ? "Required" : "Optional"}
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            <Switch
              checked={def.isActive}
              onCheckedChange={(checked) =>
                patchMutation.mutate({ id: def.id, payload: { isActive: checked } })
              }
              disabled={patchMutation.isPending}
              data-testid={`switch-active-${def.id}`}
            />
            <span className="text-xs text-muted-foreground w-14">
              {def.isActive ? "Active" : "Inactive"}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <Card data-testid="card-animal-profiles-settings">
        <CardHeader>
          <CardTitle>Animal Profiles Checklist</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="card-animal-profiles-settings">
      <CardHeader>
        <CardTitle>Animal Profiles Checklist</CardTitle>
        <CardDescription>
          Manage the items staff must complete before an animal&apos;s adoption listing is marked as
          Ready. Changes apply to all animals immediately.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">

        {/* Adoption Readiness section */}
        <div className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Adoption Readiness
          </p>
          {readinessDefs.length === 0 && (
            <p className="text-sm text-muted-foreground py-2" data-testid="text-no-readiness-definitions">
              No active readiness items.
            </p>
          )}
          <div className="divide-y">
            {readinessDefs.map((def, idx) => renderDefinitionRow(def, idx, readinessDefs))}
          </div>
        </div>

        <Separator />

        {/* Evaluation section */}
        <div className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Evaluation
          </p>
          {evaluationDefs.length === 0 && (
            <p className="text-sm text-muted-foreground py-2" data-testid="text-no-evaluation-definitions">
              No active evaluation items.
            </p>
          )}
          <div className="divide-y">
            {evaluationDefs.map((def, idx) => renderDefinitionRow(def, idx, evaluationDefs))}
          </div>
        </div>

        {inactiveDefs.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Inactive Items
              </p>
              {inactiveDefs.map((def) => (
                <div
                  key={def.id}
                  className="flex items-center justify-between gap-3 py-2 opacity-60"
                  data-testid={`row-definition-${def.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm line-through truncate" data-testid={`text-def-name-${def.id}`}>
                      {def.name}
                    </span>
                    <Badge variant="outline" className="text-xs shrink-0">
                      {def.checklistType === "readiness" ? "Readiness" : "Evaluation"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Switch
                      checked={false}
                      onCheckedChange={(checked) =>
                        patchMutation.mutate({ id: def.id, payload: { isActive: checked } })
                      }
                      disabled={patchMutation.isPending}
                      data-testid={`switch-active-${def.id}`}
                    />
                    <span className="text-xs text-muted-foreground">Inactive</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <Separator />

        {/* Add form */}
        <div className="space-y-3">
          <p className="text-sm font-medium">Add checklist item</p>

          {/* Checklist type selector */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-48 space-y-1">
              <Label className="text-xs text-muted-foreground">Checklist type</Label>
              <Select
                value={newChecklistType}
                onValueChange={(v) => {
                  setNewChecklistType(v as "readiness" | "evaluation");
                  setNewScrapedField("");
                  setNewAutoComplete(false);
                  setNewEvaluationTrackId("");
                }}
              >
                <SelectTrigger data-testid="select-new-definition-checklist-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="readiness">Adoption Readiness</SelectItem>
                  <SelectItem value="evaluation">Evaluation</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newChecklistType === "evaluation" && (
              <div className="flex-1 min-w-48 space-y-1">
                <Label className="text-xs text-muted-foreground">Evaluation track</Label>
                <Select
                  value={newEvaluationTrackId || "__core__"}
                  onValueChange={(v) => setNewEvaluationTrackId(v === "__core__" ? "" : v)}
                >
                  <SelectTrigger data-testid="select-new-definition-eval-track">
                    <SelectValue placeholder="All tracks (core item)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__core__">All tracks (core item)</SelectItem>
                    {activeTracks.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-48 space-y-1">
              <Label htmlFor="new-def-name" className="text-xs text-muted-foreground">
                Name
              </Label>
              <Input
                id="new-def-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAdd();
                }}
                placeholder="e.g. Vet check completed"
                data-testid="input-new-definition-name"
              />
            </div>
            <div className="flex-1 min-w-48 space-y-1">
              <Label htmlFor="new-def-scraped-field" className="text-xs text-muted-foreground">
                Auto-checks when (optional)
              </Label>
              <Select
                value={newScrapedField || "__none__"}
                onValueChange={(v) => {
                  const val = v === "__none__" ? "" : v;
                  setNewScrapedField(val);
                  if (!val) setNewAutoComplete(false);
                }}
              >
                <SelectTrigger data-testid="select-new-definition-scraped-field">
                  <SelectValue placeholder="None (manual only)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None (manual only)</SelectItem>
                  {SCRAPED_FIELD_OPTIONS.filter((o) => o.types.includes(newChecklistType)).map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {newScrapedField && (
                <div className="flex items-center gap-2 pt-1">
                  <Switch
                    checked={newAutoComplete}
                    onCheckedChange={setNewAutoComplete}
                    data-testid="switch-new-definition-auto-complete"
                  />
                  <Label className="text-xs text-muted-foreground cursor-pointer">
                    Auto-complete when confirmed
                  </Label>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 pb-1">
              <Switch
                checked={newIsRequired}
                onCheckedChange={setNewIsRequired}
                data-testid="switch-new-definition-required"
              />
              <Label className="text-sm cursor-pointer" htmlFor="new-req-switch">
                {newIsRequired ? "Required" : "Optional"}
              </Label>
            </div>
            <Button
              onClick={handleAdd}
              disabled={createMutation.isPending || !newName.trim()}
              data-testid="button-add-definition"
            >
              {createMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Add item
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
